import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../auth/AuthContext";
import { useGoogleLogin } from "@react-oauth/google";

export default function Login() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleClickStarted = useRef(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const goAfterLogin = (userEmail) => {
  const key = `firstLogin:${userEmail}`;
  const firstLogin = localStorage.getItem(key);

  if (!firstLogin || firstLogin === "true") {
    localStorage.setItem(key, "true");
    navigate("/welcome", { replace: true });
  } else {
    navigate("/dashboard", { replace: true });
  }
};

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const oauthMs = googleClickStarted.current == null ? null : Math.round(performance.now() - googleClickStarted.current);
      console.info("[sAInthetic auth] Google OAuth completed", { oauthMs });
      const backendStarted = performance.now();
      setGoogleLoading(true);
      try {
        setError("");

        const res = await fetch(`${BASE_URL}/api/auth/google-login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ access_token: tokenResponse.access_token }),
        });

        const data = await res.json();
        console.info("[sAInthetic auth] Google backend response", { backendMs: Math.round(performance.now() - backendStarted), status: res.status });
        if (!res.ok) throw new Error(data.error || "Google login failed");

        loginSuccess(data.token, data.user);
        goAfterLogin(data.user?.email);
      } catch (err) {
        setError(err.message || "Google login failed");
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setGoogleLoading(false);
      setError("Google login failed. Please try again.");
    },
    onNonOAuthError: () => {

      setGoogleLoading(false);
      setError("Google sign-in was interrupted. Please try again.");
    },
  });

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("All fields are required");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 403 && data.error) {
          setError(
            "Please confirm your email before logging in. Check your inbox for the verification link."
          );
          return;
        }
        throw new Error(data.error || "Login failed");
      }

      loginSuccess(data.token, data.user);
      goAfterLogin();
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    background: "#ffffff",
    border: "1.5px solid #e5e0ed",
    borderRadius: "10px",
    color: "#211a35",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f7f5fc 0%, #f2effa 100%)",
        padding: "28px 16px",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "10%",
          right: "10%",
          width: 350,
          height: 350,
          background:
            "radial-gradient(circle, rgba(255,217,69,0.25), transparent 70%)",
          borderRadius: "50%",
          filter: "blur(20px)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          background: "#ffffff",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
          WebkitBackdropFilter: isSafari ? "blur(4px)" : "blur(12px)",
          border: "1px solid #e8e2f3",
          borderRadius: "22px",
          padding: "38px 32px 30px",
          maxWidth: 440,
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 10,
          boxShadow: "0 20px 60px rgba(48, 34, 83, 0.10)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <img
            src="/sainthetic-mark.svg"
            alt="SocialSage Logo"
            style={{
              width: 48,
              height: 48,
              objectFit: "contain",
              marginBottom: "15px",
            }}
          />
          <h1
            style={{
              fontSize: "2rem",
              fontWeight: 900,
              background: "linear-gradient(96deg, #211a35 35%, #7041db 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              margin: 0,
            }}
          >
            Welcome
          </h1>
          <p style={{ color: "#716b80", margin: 0 }}>
            Sign in to your sAInthetic account
          </p>
        </div>

        <div
          role="note"
          style={{
            background: "#f5f0ff",
            border: "1px solid #e5d9ff",
            color: "#59418b",
            padding: "11px 13px",
            borderRadius: "8px",
            fontSize: "0.86rem",
            lineHeight: 1.45,
            margin: "0 0 18px",
            textAlign: "left",
          }}
        >
          <strong style={{ display: "block", marginBottom: 3 }}>First visit may take a little longer</strong>
          sAInthetic is on a free hosting plan. If the app has been inactive, the first connection can take up to a minute while the service wakes up.
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 17,
            width: "100%",
          }}
        >
          <div>
            <label
              style={{ color: "#7041db", marginBottom: 8, display: "block" }}
            >
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={inputStyle}
            />
          </div>

          <div>
            <label
              style={{ color: "#7041db", marginBottom: 8, display: "block" }}
            >
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={inputStyle}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: -8,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                color: "#7041db",
                cursor: "pointer",
              }}
              onClick={() => navigate("/forgot-password")}
            >
              Forgot password?
            </span>
          </div>

          {error && (
            <div
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                color: "#be123c",
                padding: "12px 14px",
                borderRadius: "8px",
                fontSize: "0.9rem",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              background: "#ffd945",
              color: "#211a35",
              border: "none",
              padding: "15px 20px",
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: 22,
          }}
        >
          <div
            style={{ flex: 1, height: 1, background: "#e9e4f0" }}
          />
          <span style={{ color: "#8a8496" }}>or</span>
          <div
            style={{ flex: 1, height: 1, background: "#e9e4f0" }}
          />
        </div>

        <button
          type="button"
          onClick={() => {
            setError("");
            googleClickStarted.current = performance.now();

            setGoogleLoading(true);
            loginWithGoogle();
          }}
          disabled={googleLoading}
          style={{
            width: "100%",
            background: "#ffffff",
            color: "#211a35",
            border: "1.5px solid #e5e0ed",
            borderRadius: "10px",
            padding: "12px 16px",
            fontWeight: 600,
            marginTop: 10,
            cursor: googleLoading ? "wait" : "pointer",
            opacity: googleLoading ? 0.7 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <img src="/google-icon.svg" alt="Google logo" style={{ width: 18 }} />
          {googleLoading ? "Connecting to Google…" : "Continue with Google"}
        </button>

        <p
          style={{
            textAlign: "center",
            color: "#716b80",
            fontSize: "0.9rem",
            marginTop: 25,
          }}
        >
          Don’t have an account?{" "}
          <Link
            to="/register"
            style={{
              color: "#7041db",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Sign Up
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
